// Dashboard 5 — User Segment Performance. How messaging performs across user
// engagement states and dominant life pillars. Two comparison tables; only
// segments with traffic appear.

import React from "react";
import { Skeleton } from "../../../ui/skeleton";
import { Panel } from "../../../Components/admin/reporting/controls";
import { getEngagementSegments } from "../../../Services/adminEngagement";
import {
  ENGAGEMENT_STATE_LABELS,
  LIFE_PILLAR_LABELS,
  Rate,
  ErrorBox,
  EmptyState,
  WindowBadge,
  fmtInt,
  fmtPctValue,
  labelFor,
  useEngagementData,
} from "./engagementShared";

const SegmentTable = ({ rows, labelMap, headLabel }) => {
  const entries = Object.entries(rows || {}).sort(
    (a, b) => (b[1]?.messages_sent || 0) - (a[1]?.messages_sent || 0)
  );
  if (entries.length === 0) {
    return <EmptyState>No segment traffic in this window.</EmptyState>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3 font-medium">{headLabel}</th>
            <th className="py-2 px-3 text-right font-medium">Messages sent</th>
            <th className="py-2 px-3 text-right font-medium">Sessions</th>
            <th className="py-2 px-3 text-right font-medium">Session start</th>
            <th className="py-2 px-3 text-right font-medium">Conv %</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([key, v]) => (
            <tr key={key} className="border-b transition-colors hover:bg-slate-50">
              <td className="py-2.5 pr-3 align-middle font-medium">
                {labelFor(labelMap, key)}
              </td>
              <td className="py-2.5 px-3 text-right align-middle tabular-nums">
                {fmtInt(v.messages_sent)}
              </td>
              <td className="py-2.5 px-3 text-right align-middle tabular-nums">
                {fmtInt(v.sessions_generated)}
              </td>
              <td className="py-2.5 px-3 text-right align-middle">
                <Rate value={v.session_start_rate} available />
              </td>
              <td className="py-2.5 px-3 text-right align-middle font-semibold tabular-nums">
                {fmtPctValue(v.conversion_pct)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SegmentTab = ({ range }) => {
  const { data, loading, error } = useEngagementData(
    () => getEngagementSegments(range),
    [range]
  );

  if (error) return <ErrorBox>{error}</ErrorBox>;

  return (
    <div className="space-y-5">
      <WindowBadge window={data?.window} timezone={data?.timezone} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="By engagement state">
          {loading && !data ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <SegmentTable
              rows={data?.by_engagement_state}
              labelMap={ENGAGEMENT_STATE_LABELS}
              headLabel="State"
            />
          )}
        </Panel>
        <Panel title="By life pillar">
          {loading && !data ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <SegmentTable
              rows={data?.by_life_pillar}
              labelMap={LIFE_PILLAR_LABELS}
              headLabel="Pillar"
            />
          )}
        </Panel>
      </div>
    </div>
  );
};

export default SegmentTab;
